"use client";

import Link from "next/link";
import { useContext, useEffect, useState } from "react";
import { Button, Grid, Modal } from "antd";
import GOOGLE_PLAY from "@/public/google-play.svg";
import { usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import { IconBrandAppleFilled } from "@/lib/icons";
import { useStorePlatform } from "@/lib/useStorePlatform";

import { usercontext } from "@/lib/context";
import { EXTERNAL_REL } from "@/lib/externalLink";

const CTA_CONTENT = {
  homepage: {
    text: "Discover events faster on Tykkit",
    modalText: "Explore events & book instantly",
  },
  event: {
    text: "Book tickets faster on app",
    modalText: "Get best seats before they sell out!",
  },
  checkout: {
    text: "Get instant booking alerts",
    modalText: "Track bookings & updates instantly",
  },
  default: {
    text: "Get faster booking on Tykkit",
    modalText: "Download Tykkit App",
  },
};

const ONE_DAY = 24 * 60 * 60 * 1000;
const DISMISS_KEY = "app_cta_dismissed_at";

const AppDownloadCTA = ({ page: initialPage = "default" }) => {
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(initialPage);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useContext(usercontext);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  // ✅ Mobile only (<768px)
  const isMobile = !screens.md;

  // 📍 Dynamic page detection
  useEffect(() => {
    if (pathname === "/") {
      setPage("homepage");
    } else if (pathname?.includes("/book")) {
      setPage("checkout");
    } else if (
      pathname?.includes("/events/") ||
      pathname?.includes("/event/")
    ) {
      setPage("event");
    } else {
      setPage("default");
    }
  }, [pathname]);

  const content = CTA_CONTENT[page] || CTA_CONTENT.default;

  // 🔥 Show only on mobile/tablet
  useEffect(() => {
    if (typeof window === "undefined") return;

    // ❌ Hide completely on desktop/tablet (>=768px)
    if (!isMobile) {
      setShowModal(false);
      return;
    }

    const lastDismissed = localStorage.getItem(DISMISS_KEY);

    const isRecentlyDismissed =
      lastDismissed && Date.now() - Number(lastDismissed) < ONE_DAY;

    // ❌ Recently closed
    if (isRecentlyDismissed) {
      setShowModal(false);
      return;
    }

    // ✅ Show modal
    setShowModal(true);
  }, [isMobile]);

  const handleClose = () => {
    localStorage.setItem(DISMISS_KEY, Date.now());
    setShowModal(false);
  };

  // ❌ Hide on the seating page or inside the booking application webview
  if (pathname?.includes("/seating")) return null;
  if (searchParams?.get("is_application") === "true") return null;

  // ❌ Hide inside app webview
  if (user?.is_webview) return null;

  // ❌ Hide on tablet and desktop
  if (!isMobile) return null;

  return (
    <DownloadModal
      open={showModal}
      onClose={handleClose}
      text={content.modalText}
    />
  );
};

// 🔵 DOWNLOAD BANNER
const DownloadModal = ({ open, onClose, text }) => {
  const platform = useStorePlatform();
  const isIOS = platform === "ios";

  const iosUrl = process.env.NEXT_PUBLIC_IOS_APP;
  const androidUrl =
    process.env.NEXT_PUBLIC_ANDROID_STORE_LINK ||
    process.env.NEXT_PUBLIC_MOBILE_APP;

  const appLink = isIOS ? iosUrl : androidUrl;

  // Don't show if the banner is closed OR if there is no URL configured for this platform
  if (!open || !appLink) return null;

  const appIcon = isIOS ? (
    <div
      style={{
        width: 36,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconBrandAppleFilled size={32} color="#021526" />
    </div>
  ) : (
    <Image src={GOOGLE_PLAY} alt="Google Play" width={36} height={36} />
  );

  return (
    <div className="fixed bottom-[60px] left-0 right-0 z-50 bg-white border-t border-gray-200 p-3 shadow-lg flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">{appIcon}</div>
        <div>
          <h2 className="text-sm font-semibold m-0 text-gray-800">
            Never miss an event
          </h2>
          <p className="text-xs text-gray-500 m-0">Download the Tykkit app</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Link href={appLink} rel={EXTERNAL_REL}>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              localStorage.setItem(DISMISS_KEY, Date.now());
              onClose();
            }}
          >
            Get App
          </Button>
        </Link>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-1"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export default AppDownloadCTA;
